import Container from "react-bootstrap/Container";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, Phone } from "lucide-react";
import localizedLink from "../../utils/localizedLink";
import { openCookieSettings } from "../../utils/cookieConsent";

const CONTACTS = [
    { label: "574 180 111", href: "tel:574180111", Icon: Phone },
    { label: "info@eufy.ge", href: "mailto:info@eufy.ge", Icon: Mail },
];

const SOCIALS = [
    { label: "Facebook", href: "https://www.facebook.com/eufygeorgia", Icon: Facebook },
    { label: "Instagram", href: "https://www.instagram.com/eufy.ge", Icon: Instagram },
];

const POLICIES = [
    { key: "warranty", to: "/policies/warranty" },
    { key: "delivery", to: "/policies/delivery" },
    { key: "returns", to: "/policies/returns" },
    { key: "privacy-policy", to: "/policies/privacy" },
    { key: "terms-and-conditions", to: "/policies/terms-and-conditions" },
];

const APP_STORES = [
    {
        label: "App Store",
        href: "https://apps.apple.com/us/app/eufy-security/id1424956516",
        src: "/images/app-store.svg",
    },
    {
        label: "Google Play",
        href: "https://play.google.com/store/apps/details?id=com.oceanwing.battery.cam",
        src: "/images/google-play.svg",
    },
];

// White circles on the gray footer, like the icons on the home page's benefit
// cards; they turn near-black on hover, like eufy.com's primary buttons.
// Bracket values where Bootstrap's !important utilities (p-4, gap-3, bg-white...)
// would win, e.g. its bg-white would keep the circle white on hover.
const ICON_CIRCLE_CLASS_NAME =
    "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#fff] text-[#080a0f] transition-colors";
const SECTION_TITLE_CLASS_NAME =
    "mb-[16px] text-base font-semibold leading-snug text-[#080a0f]";

export default function Footer() {
    const { t, i18n } = useTranslation();

    return (
        <footer className="parallax-reveal mt-[56px] overflow-hidden rounded-t-[28px] bg-[#f5f6f7] md:mt-[80px] md:rounded-t-[40px]">
            <Container className="parallax-reveal-content pt-[40px] md:pt-[56px]">
                <div className="grid gap-x-[32px] gap-y-[36px] sm:grid-cols-2 lg:grid-cols-[1.1fr_0.9fr_1.2fr_1.1fr]">
                    <div>
                        <Link
                            to={localizedLink("/", i18n.language)}
                            className="text-lg font-semibold leading-tight text-[#080a0f]"
                        >
                            {t("header.eufy-georgia")}
                        </Link>
                        <p className="mt-[8px] max-w-xs text-sm leading-snug text-[#6d6e71]">
                            {t("header.slogan")}
                        </p>
                        <ul className="mt-[20px] flex gap-[8px]">
                            {SOCIALS.map(({ label, href, Icon }) => (
                                <li key={label}>
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={label}
                                        title={label}
                                        className={`${ICON_CIRCLE_CLASS_NAME} hover:bg-[#080a0f] hover:text-white`}
                                    >
                                        <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <h2 className={SECTION_TITLE_CLASS_NAME}>{t("footer.contact")}</h2>
                        <ul className="space-y-[10px]">
                            {CONTACTS.map(({ label, href, Icon }) => (
                                <li key={href}>
                                    <a
                                        href={href}
                                        className="group flex items-center gap-[12px] font-medium text-[#080a0f]"
                                    >
                                        <span
                                            className={`${ICON_CIRCLE_CLASS_NAME} group-hover:bg-[#080a0f] group-hover:text-white`}
                                        >
                                            <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
                                        </span>
                                        {label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <nav aria-label={t("footer.policies")}>
                        <h2 className={SECTION_TITLE_CLASS_NAME}>{t("footer.policies")}</h2>
                        <ul className="space-y-[10px]">
                            {POLICIES.map(({ key, to }) => (
                                <li key={key}>
                                    <Link
                                        to={localizedLink(to, i18n.language)}
                                        className="text-sm text-[#6d6e71] transition-colors hover:text-[#080a0f] md:text-[15px]"
                                    >
                                        {t(`footer.${key}`)}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div>
                        <h2 className={SECTION_TITLE_CLASS_NAME}>{t("footer.download-app")}</h2>
                        <div className="flex items-center gap-[12px]">
                            <img
                                src="/images/eufy-security-app.webp"
                                alt=""
                                className="h-12 w-12 rounded-xl"
                            />
                            <span className="font-medium text-[#080a0f]">eufy Security</span>
                        </div>
                        <div className="mt-[14px] flex flex-wrap gap-[8px]">
                            {APP_STORES.map(({ label, href, src }) => (
                                <a
                                    key={label}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="transition-opacity hover:opacity-80"
                                >
                                    <img src={src} alt={label} className="h-[38px] w-auto" />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="mt-[40px] border-t border-black/[0.06] py-[24px] text-xs text-[#6d6e71] md:mt-[56px]">
                    <div className="flex flex-wrap justify-between gap-x-[16px] gap-y-[6px]">
                        <p>
                            © {new Date().getFullYear()} {t("header.eufy-georgia")}. {t("footer.rights")}
                        </p>
                        <button
                            type="button"
                            onClick={openCookieSettings}
                            className="transition-colors hover:text-[#080a0f]"
                        >
                            {t("cookies.settings")}
                        </button>
                    </div>
                    {/* Hidden until the company's details are filled in. */}
                    {t("company.name") && <p className="mt-[6px]">{t("company.details")}</p>}
                </div>
            </Container>
        </footer>
    );
}
